import React, { useEffect, useState } from 'react';
import safetySystemService from '../../../services/safetySystem.service';
import type { SafetyUnitNotification } from '../../../types/safetySystem';
import { errorText, fmtDateTime } from './safetyDrawerShared';

/**
 * Unit drawer Notifications tab (Phase 5 Task 7): GET /units/{r}/notifications — the
 * e-mails logged against the unit and its current owner (V1 `viewemailnotification`),
 * newest first. Date | To | Subject | Source, each row expandable to its plain-text body.
 * Loads when the tab opens.
 */

const NOTIFICATION_SOURCES: Record<string, string> = { unit: 'Unit', owner: 'Owner' };
const th = 'px-1.5 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted';

export default function UnitNotificationsTab({ unitrecno }: { unitrecno: number }) {
  const [rows, setRows] = useState<SafetyUnitNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setExpanded(new Set());
    safetySystemService.unitNotifications(unitrecno, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setRows(data ?? []); })
      .catch((e) => { if (!controller.signal.aborted) setError(errorText(e)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [unitrecno]);

  const toggle = (recno: number) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(recno)) next.delete(recno); else next.add(recno);
    return next;
  });

  if (loading) return <div className="py-6 text-center text-sm text-muted">Loading notifications…</div>;
  if (error) return <div role="alert" className="rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{error}</div>;
  if (!rows.length) return <div className="py-6 text-center text-sm text-muted">No notifications have been sent for this unit.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label="Notifications">
        <thead>
          <tr>
            <th className={th}>Date</th><th className={th}>To</th><th className={th}>Subject</th><th className={th}>Source</th>
            <th className={th}><span className="sr-only">Body</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((n) => {
            const isOpen = expanded.has(n.recno);
            return (
              <React.Fragment key={`${n.source}-${n.recno}`}>
                <tr className="cursor-pointer border-t border-rule hover:bg-highlight" onClick={() => toggle(n.recno)}>
                  <td className="whitespace-nowrap px-1.5 py-1">{fmtDateTime(n.datetime)}</td>
                  <td className="px-1.5 py-1">{n.emailto}</td>
                  <td className="px-1.5 py-1">{n.subject}</td>
                  <td className="px-1.5 py-1">{NOTIFICATION_SOURCES[n.source] ?? n.source}</td>
                  <td className="px-1.5 py-1 text-right">
                    <button type="button" aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} notification ${n.recno}`}
                      onClick={(e) => { e.stopPropagation(); toggle(n.recno); }} className="text-xs font-medium text-accent-text hover:underline">
                      {isOpen ? 'Hide' : 'Show'}
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr className="border-t border-rule bg-highlight/40">
                    <td colSpan={5} className="px-3 py-2">
                      <pre aria-label={`Notification ${n.recno} body`} className="whitespace-pre-wrap break-words font-sans text-sm">{n.body}</pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
